import type { Datum } from '@antv/g2plot'
import { Pie } from '@antv/g2plot'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyView, Trans } from '@/components'
import { formatFloat } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { GPU_CHART_COLORS } from '../constants/colors'
import { SpecCategory } from '../constants/specCategory'

export interface PieChartData {
  type: string
  nodeCount: number
  gpuCount: number
  category: number
}

function getUnitLabel(category: number, t: (key: string) => string) {
  if (category === SpecCategory.CPU) return t('common.cores')
  return t('common.instances')
}

interface AcceleratorStats {
  /** Total available accelerators */
  available: number
  /** Number of standby accelerators */
  standby: number
  /** Number of accelerators in use */
  inUse: number
  /** Current usage rate (percentage) */
  usageRate: number
}

interface AcceleratorCardProps {
  /** Pie chart data (max 5 items) */
  pieChartData: PieChartData[]
  /** Accelerator statistics */
  stats: AcceleratorStats
  /** Custom styles */
  className?: string
}

// Legend Item Component
interface LegendItemProps {
  type: string
  nodeCount: number
  gpuCount: number
  category: number
  color: string
  total: number
}

function LegendItem({ type, nodeCount, gpuCount, category, color, total }: LegendItemProps) {
  const { t } = useTranslation('privateRegion')
  const percentage = ((nodeCount / total) * 100).toFixed(1)

  return (
    <div className="flex flex-col items-start justify-start mb-0.5">
      <div className="w-full">
        <div className="flex items-center gap-x-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
          <div className="t-sm">{type}</div>
        </div>
      </div>
      <div className="flex justify-start items-center gap-x-2 ml-4">
        <div className="t-xs text-secondary bg-neutral-100 px-1.5 py-1 rounded inline-block">
          <span>
            {nodeCount} {getUnitLabel(category, t)}
          </span>
          <span className="ml-2">{percentage}%</span>
        </div>
        {gpuCount > 0 && (
          <div className="t-xs text-secondary bg-neutral-100 px-1.5 py-1 rounded inline-block">
            <span>{gpuCount} vGPUs</span>
          </div>
        )}
      </div>
    </div>
  )
}

// Pie Chart Component
function GPUPieChart({ data }: { data: PieChartData[] }) {
  const { t } = useTranslation('privateRegion')
  const containerRef = useRef<HTMLDivElement>(null)
  const plotRef = useRef<Pie | null>(null)

  const total = data.reduce((sum, item) => sum + item.nodeCount, 0)
  const hasNoData =
    !data || data.length === 0 || total === 0 || (data.length === 1 && data[0].type === 'No Data')

  useEffect(() => {
    if (hasNoData) return
    if (!containerRef.current) return
    if (!data || data.length === 0) return
    if (plotRef.current) {
      plotRef.current.destroy()
      plotRef.current = null
    }

    plotRef.current = new Pie(containerRef.current, {
      height: 280,
      appendPadding: 0,
      data,
      angleField: 'nodeCount',
      colorField: 'type',
      radius: 1,
      innerRadius: 0.6,
      color: (datum: Datum) => {
        const index = data.findIndex((item) => item.type === (datum as PieChartData).type)
        return GPU_CHART_COLORS[index % GPU_CHART_COLORS.length]
      },
      label: {
        content: '',
      },
      interactions: [{ type: 'element-selected' }],
      tooltip: {
        follow: true,
        domStyles: {
          'g2-tooltip': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            padding: 0,
            border: 'none',
            pointerEvents: 'none', // 防止 tooltip 阻擋滑鼠事件
          },
        },
        customContent: (_title: string, items: any[]) => {
          if (!items || items.length === 0) return ''
          const item = items[0]
          const percentage = ((item.data.nodeCount / total) * 100).toFixed(1)
          const nodesText = getUnitLabel(item.data.category, t)
          const percentageText = t('common.percentage')
          return `
            <div style="min-width: 200px; display: flex; flex-direction: column; gap: 8px; background-color: #000; color: #fff; padding: 8px 12px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
              <div style="display: flex; align-items: center; gap: 8px;">
                <div style="width: 6px; height: 6px; border-radius: 50%; background-color: ${item.color};"></div>
                <div style="font-size: 12px; line-height: 16px; font-weight: 600; color: rgba(255, 255, 255, 0.65);">${item.name}</div>
              </div>
              <div style="height: 1px; width: 100%; background-color: rgba(255, 255, 255, 0.35);"></div>
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div style="font-size: 12px; line-height: 16px; font-weight: 600; color: rgba(255, 255, 255, 0.65);">${nodesText}</div>
                <div style="font-size: 12px; line-height: 16px; font-weight: 600; color: white;">${item.data.nodeCount}</div>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div style="font-size: 12px; line-height: 16px; font-weight: 600; color: rgba(255, 255, 255, 0.65);">${percentageText}</div>
                <div style="font-size: 12px; line-height: 16px; font-weight: 600; color: white;">${percentage}%</div>
              </div>
            </div>
          `
        },
      },
      legend: false,
      statistic: {
        title: {
          // @ts-expect-error - G2Plot types are not fully compatible
          customHtml: (container: any, view: any, datum: any, data: any) => {
            const total = data
              ? (data as PieChartData[]).reduce((sum: number, item: any) => sum + item.nodeCount, 0)
              : 0
            const totalText = t('common.total')
            return `<div class="flex flex-col items-center justify-center">
            <div class="text-blue-600 text-2xl font-bold">${total}</div>
            <div class="text-secondary text-sm font-semibold">${totalText}</div>
            </div>`
          },
        },
        content: false,
      },
    })
    plotRef.current.render()

    return () => {
      plotRef.current?.destroy()
      plotRef.current = null
    }
  }, [data, total, hasNoData])

  // If no data, show EmptyView instead of chart
  if (hasNoData) {
    return (
      <div className="w-full h-[280px] flex items-center justify-center">
        <EmptyView
          title={
            <Trans ns="privateRegion" i18nKey="common.noData">
              No data
            </Trans>
          }
        />
      </div>
    )
  }

  return (
    <div className="flex gap-x-4 w-full overflow-visible">
      <div ref={containerRef} className="relative w-[280px] shrink-0 overflow-visible" />
      <div className="flex-1 min-w-0 grid grid-cols-1 space-y-3 overflow-y-auto">
        {data.map((item, index) => (
          <LegendItem
            key={item.type}
            type={item.type}
            nodeCount={item.nodeCount}
            gpuCount={item.gpuCount}
            category={item.category}
            color={GPU_CHART_COLORS[index % GPU_CHART_COLORS.length]}
            total={total}
          />
        ))}
      </div>
    </div>
  )
}

// Stat Card Component
interface SimpleStatCardProps {
  title: string
  value: number
  valueType?: 'number' | 'percentage'
}

function SimpleStatCard({ title, value, valueType = 'number' }: SimpleStatCardProps) {
  const formatValue = (val: number) => {
    if (valueType === 'percentage') {
      return `${formatFloat(val)}%`
    }
    return formatFloat(val, 0)
  }

  return (
    <div className="bg-panel-white rounded-2xl p-6 border border-neutral-200">
      <div className="flex flex-col gap-y-4">
        <div className="t-sb">{title}</div>
        <div className="h3">{formatValue(value)}</div>
      </div>
    </div>
  )
}

// Main Component
export function AcceleratorCard({ pieChartData, stats, className }: AcceleratorCardProps) {
  const { t } = useTranslation('privateRegion')
  return (
    <div
      className={cn('w-full bg-panel-white rounded-2xl p-6 border border-neutral-200', className)}
    >
      <div className="flex lg:flex-row justify-between space-x-4">
        <div className="w-full lg:w-2/5 flex items-center space-x-6 py-6">
          <div className="w-full h-full">
            <GPUPieChart data={pieChartData.slice(0, 5)} />
          </div>
        </div>

        <div className="w-full lg:w-3/5 grid grid-cols-2 gap-6">
          <SimpleStatCard
            title={t('accelerator.availableGpus')}
            value={stats.available}
            valueType="number"
          />
          <SimpleStatCard
            title={t('accelerator.standbyGpus')}
            value={stats.standby}
            valueType="number"
          />
          <SimpleStatCard
            title={t('accelerator.gpusInUse')}
            value={stats.inUse}
            valueType="number"
          />
          <SimpleStatCard
            title={t('accelerator.currentUsageRate')}
            value={stats.usageRate}
            valueType="percentage"
          />
        </div>
      </div>
    </div>
  )
}

export default AcceleratorCard
